import { Alert, SubmitButton, Typography } from 'omakase';
import { ForgotPasswordValues } from 'routes/unprotected/ForgotPasswordRoute';
import { Form, Formik, FormikErrors, FormikHelpers, FormikProps } from 'formik';
import { FormTextField, Grid } from 'ui';
import { createRef } from 'react';
import { isEmail } from '@tock/shared/utils/FormValidators';
import { lib } from 'types';
import BaseComponent from '@tock/shared/components/BaseComponent';
import ButtonGroup from '@tock/shared/components/buttons/ButtonGroup';
import _ from 'lodash';

//exported for testing
export const validate = (values: ForgotPasswordValues) => {
  const errors: FormikErrors<ForgotPasswordValues> = {};
  if (!values.email || _.size(_.trim(values.email)) <= 0) {
    errors.email = 'Please enter an email';
  } else if (!isEmail(values.email)) {
    errors.email = 'Please enter a valid email';
  }
  return errors;
};

// This is exported for testing. The return value is also used for testing;
// the tests await on the return value (which is promise-shaped) before
// checking further expectations.
export const handleSubmitSeam = (
  onSubmitProp: Props['onSubmit'],
  values: ForgotPasswordValues,
  actions: FormikHelpers<ForgotPasswordValues>
) => {
  actions.setStatus('');
  return onSubmitProp(values)
    .then(() => {
      actions.setSubmitting(false);
    })
    .catch((error: lib.SingleResponseProto) => {
      let actionStatusString: string;
      // Error code numbers start at 1000 and go up from there. A -1 indicates
      // that there was no errorCode and we go with the default behavior.
      // Look at com/tocktix/handlers/authentication/PasswordResetHandler.java
      // for a rundown of the possible errors the validator can return.
      switch (Number(error?.error?.errorCode ?? -1)) {
        case 1001:
          actionStatusString = 'Email address not found';
          break;
        case 2002:
          actionStatusString =
            'This account is managed using single-sign-on. Emailing you a password reset link is not allowed';
          break;
        case 5007:
          actionStatusString = 'This account is locked, you may not reset its password';
          break;
        default:
          actionStatusString =
            'We encountered a problem processing this request to reset your password. You can try again. It may also help to clear your browser cookies or try this request from a different browser';
          break;
      }
      actions.setStatus(actionStatusString);
      actions.setSubmitting(false);
    });
};

interface Props {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onSubmit(values: ForgotPasswordValues): Promise<any>;
}

class ForgotPasswordForm extends BaseComponent<Props> {
  emailInput = createRef<HTMLInputElement>();

  componentDidMount() {
    if (this.emailInput.current) {
      this.emailInput.current.focus();
    }
  }

  handleSubmit(values: ForgotPasswordValues, actions: FormikHelpers<ForgotPasswordValues>) {
    handleSubmitSeam(this.props.onSubmit, values, actions);
  }

  render() {
    return (
      <Formik<ForgotPasswordValues>
        initialValues={{
          email: '',
        }}
        onSubmit={this.handleSubmit}
        enableReinitialize={false}
        validate={validate}
      >
        {(props: FormikProps<ForgotPasswordValues>) => (
          <Form>
            <Grid container spacing={1}>
              {!_.isEmpty(props.errors) && props.submitCount > 0 && !props.status && (
                <Grid item xs={12}>
                  <Alert severity="error">Oops there are errors on the form</Alert>
                </Grid>
              )}
              {props.status && (
                <Grid item xs={12}>
                  <Alert severity="error">{props.status}</Alert>
                </Grid>
              )}
              <Grid item xs={12}>
                <Typography component="span" variant="body02" gutterBottom>
                  Enter the email you signed up with below and we'll send you instructions on how to
                  securely reset your password.
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <FormTextField
                  fullWidth
                  id="email"
                  name="email"
                  type="email"
                  label="Email"
                  inputProps={{
                    'data-testid': 'email-input',
                  }}
                />
              </Grid>
            </Grid>
            <div className="FormActions">
              <ButtonGroup>
                <SubmitButton
                  data-testid="signin"
                  isSubmitting={props.isSubmitting}
                  variant="primary"
                  size="large"
                  fullWidth
                >
                  Send instructions
                </SubmitButton>
              </ButtonGroup>
            </div>
          </Form>
        )}
      </Formik>
    );
  }
}

export default ForgotPasswordForm;
